/**
 * UrgentCreditModal - Warning at <2 Reader Credits
 * 
 * More urgent warning shown when user's balance drops below 2 RC.
 * Respects a 4-hour cooldown between prompts (more frequent than low credit).
 */
import React from 'react';
import { X, AlertOctagon, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useReaderFunnel } from '@/contexts/ReaderFunnelContext';
import { useReaderCredits } from '@/contexts/ReaderCreditContext';

export default function UrgentCreditModal() {
  const { 
    showUrgentCreditModal, 
    setShowUrgentCreditModal, 
    setShowPricingModal 
  } = useReaderFunnel();
  const { balance } = useReaderCredits();
  
  if (!showUrgentCreditModal) return null;
  
  const handleChoosePlan = () => {
    setShowUrgentCreditModal(false);
    setShowPricingModal(true);
  };
  
  const handleDismiss = () => {
    setShowUrgentCreditModal(false);
  };
  
  const displayBalance = typeof balance === 'number' ? balance.toFixed(2) : '—';
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && handleDismiss()}
    >
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-orange-500/30 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 text-center">
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-700/50 transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
          
          {/* NewAudia Logo */}
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-12 mx-auto mb-3 rounded-lg"
          />
          
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="p-3 bg-orange-600/20 rounded-xl animate-pulse">
              <AlertOctagon className="w-6 h-6 text-orange-400" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-white">Almost Out of Reader Credits</h2>
        </div>
        
        {/* Content */}
        <div className="px-6 pb-6">
          <p className="text-slate-300 text-sm leading-relaxed mb-6">
            You're down to <span className="text-orange-400 font-semibold">{displayBalance} Reader Credits</span>. 
            If you want uninterrupted playback and Ask Audia access, choose a plan now.
          </p>
          
          {/* Urgent balance indicator */}
          <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 rounded-xl p-4 mb-6 border border-orange-500/20">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Credits Remaining</span>
              <span className="text-3xl font-bold text-orange-400">{displayBalance}</span>
            </div>
            <div className="mt-3 text-xs text-orange-300/80">
              ⚠️ Running out soon — playback will pause at 0 credits
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleChoosePlan}
              className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-semibold py-3"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Choose Plan
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="w-full text-slate-400 hover:text-slate-300"
            >
              Not Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
